/**
 * Publicações: the campaign's posts as a feed of cards or a list, one container
 * for both. Every toolbar choice (window, kind, Recorte, ordering, view, tags)
 * lives in the URL and is remembered per campaign (`useViewSearch`), so the
 * sidebar's Visão geral/Lista children, a reload and a return all agree.
 *
 * The rows arrive a keyset page at a time (`usePostsFeed`), kind and Recorte
 * filtered by the route; the next page loads when the sentinel under the list
 * scrolls into view. The loaded page count rides in the history entry, so
 * Back from a post reloads as many pages as the reader had open and the
 * router's scroll restoration lands where they left.
 *
 * The only thing computed here is ordering (`orderPosts`); every figure arrives
 * per row from the route. There is no batch route for a post's metrics or
 * sentiment (each is scoped to one post), so this is one request per post per
 * figure kind; `postMetricsQueryOptions`/`sentimentQueryOptions` are the same
 * options `usePostMetrics`/`useSentiment` build, so a reader who opens a post
 * afterwards reads this cache instead of asking again.
 */

import { useQueries, type InfiniteData, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Page, PageHeader } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { useViewSearch } from '../../../chrome/viewSearch';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Button } from '../../../ui/button';
import { useAccountTags } from '../../campaign/accounts/api/queries';
import { useApiClient } from '../../session/client';
import {
  postMetricsQueryOptions,
  sentimentQueryOptions,
  usePostsFeed,
  type FeedKind,
  type Window,
} from '../api/queries';
import { pub } from './messages';
import type { CardMenu } from '../shared/PostMenu';
import {
  KIND_FILTER_KEYS,
  PublicationsToolbar,
  type KindFilterKey,
  type RecorteKey,
  type ToolbarState,
  type ViewKey,
} from './PublicationsToolbar';
import { PublicationsView } from './PublicationsView';
import {
  figuresOf,
  isStory,
  orderPosts,
  readingOf,
  scoreOf,
  SORT_KEYS,
  type PostEntry,
  type PostFigures,
  type SortKey,
} from './readings';
import { WINDOW_KEYS, windowFor, type WindowKey } from './window';
import type { Posts, PostMetrics, PostRow } from '../api/schemas';

export function usePostReadings(
  campaignId: string,
  tenantId: string,
  posts: PostRow[],
  window: Window,
): PostEntry[] {
  const client = useApiClient();

  // A story carries no figures or sentiment, so its query stays disabled
  // rather than asking a route that has nothing to answer.
  const metrics = useQueries({
    queries: posts.map((post) => ({
      ...postMetricsQueryOptions(client, campaignId, tenantId, post.post_id, { window }),
      enabled: !isStory(post),
    })),
  });
  const sentiment = useQueries({
    queries: posts.map((post) => ({
      ...sentimentQueryOptions(client, campaignId, tenantId, { scopeRef: post.post_id, window }),
      enabled: !isStory(post),
    })),
  });

  return posts.map((post, index) => ({
    post,
    figures: readingOf<PostMetrics, PostFigures>(metrics[index]!, figuresOf),
    sentiment: readingOf(sentiment[index]!, scoreOf),
  }));
}

export interface PublicationsProps {
  campaignId: string;
  tenantId: string;
  /** Seeds the tag filter when neither the URL nor a remembered choice has one. */
  initialTag?: string;
  /** Where a post opens; unset renders the posts without a link. */
  hrefFor?: (entry: PostEntry) => string;
  /** Controls the feed/list presentation from outside; unset, it lives in the
   *  URL beside the other filters. */
  view?: ViewKey;
  onViewChange?: (view: ViewKey) => void;
  /** Whether the reader may reclassify a post from its ⋯ menu; the route
   *  answers it from the session, and anything that does not ask is
   *  read-only. */
  canReclassify?: boolean;
}

/** The history-state key holding how many feed pages the reader had open. */
const PAGES_STATE = 'publicationsPages';

function openPages(): number | null {
  const state = window.history.state as Record<string, unknown> | null;
  const pages = state?.[PAGES_STATE];
  return typeof pages === 'number' ? pages : null;
}

function rememberPages(pages: number): void {
  window.history.replaceState({ ...window.history.state, [PAGES_STATE]: pages }, '');
}

type Feed = ReturnType<typeof usePostsFeed>;

/** Loads the next page once the element scrolls within a screen of view, and
 *  reloads as many pages as the history entry says were open. */
function useNextPageSentinel(feed: Feed): (node: HTMLElement | null) => void {
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed;
  // A page count, not a figure: before the first page lands, none are loaded.
  const loaded = feed.data === undefined ? 0 : feed.data.pages.length;
  const observer = useRef<IntersectionObserver | null>(null);
  // Read once, before this mount's own first page overwrites it.
  const [wanted] = useState(openPages);
  const restoring = wanted !== null && loaded < wanted && hasNextPage;

  useEffect(() => {
    if (loaded > 0 && !restoring) rememberPages(loaded);
  }, [loaded, restoring]);
  useEffect(() => {
    if (loaded > 0 && restoring && !isFetchingNextPage) void fetchNextPage();
  }, [loaded, restoring, isFetchingNextPage, fetchNextPage]);

  return (node) => {
    observer.current?.disconnect();
    if (node === null) return;
    observer.current = new IntersectionObserver(
      (seen) => {
        if (seen.some((entry) => entry.isIntersecting) && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '100% 0px' },
    );
    observer.current.observe(node);
  };
}

function PublicationsBody(props: {
  campaignId: string;
  tenantId: string;
  rows: PostRow[];
  range: Window;
  state: ToolbarState;
  hrefFor?: (entry: PostEntry) => string;
  menu: CardMenu;
}): ReactNode {
  const entries = usePostReadings(props.campaignId, props.tenantId, props.rows, props.range);
  return (
    <PublicationsView
      view={props.state.view}
      entries={orderPosts(entries, props.state.sort)}
      hrefFor={props.hrefFor}
      menu={props.menu}
    />
  );
}

const RECORTE_KEYS: RecorteKey[] = ['all', 'related', 'unrelated'];

const WINDOW_STEP = 5 * 60_000;

export function Publications(props: PublicationsProps): ReactNode {
  const [values, set] = useViewSearch(`publications.${props.campaignId}`, {
    view: { default: 'feed', values: ['feed', 'list'] },
    window: { default: '1d', values: WINDOW_KEYS },
    sort: { default: 'recent', values: SORT_KEYS },
    // Opens on every post: a post is related only once its classification
    // ran, so a related-first view hides everything a fresh collection just
    // stored (owner ruling, 2026-09-27).
    recorte: { default: 'all', values: RECORTE_KEYS },
    kind: { default: 'all', values: KIND_FILTER_KEYS },
    tags: { default: props.initialTag ?? '' },
  });
  const tags = useMemo(() => (values.tags === '' ? [] : values.tags.split(',')), [values.tags]);
  const toolbarState: ToolbarState = {
    view: props.view ?? (values.view as ViewKey),
    window: values.window as WindowKey,
    sort: values.sort as SortKey,
    tags,
  };
  const recorte = values.recorte as RecorteKey;
  const kindFilter = values.kind as KindFilterKey;
  // Ends on the current five-minute mark: a range recomputed from the clock on
  // every render would be a new query key each time, and one fixed per mount
  // would miss the cached pages when the reader comes Back from a post.
  const range = useMemo(
    () =>
      windowFor(toolbarState.window, new Date(Math.floor(Date.now() / WINDOW_STEP) * WINDOW_STEP)),
    [toolbarState.window],
  );
  const menu: CardMenu = {
    campaignId: props.campaignId,
    tenantId: props.tenantId,
    canReclassify: props.canReclassify ?? false,
  };

  const tagOptions = useAccountTags(props.campaignId);
  const feed = usePostsFeed(props.campaignId, props.tenantId, {
    window: range,
    tags,
    related: recorte === 'all' ? undefined : recorte === 'related',
    kind: kindFilter === 'all' ? undefined : (kindFilter as FeedKind),
  });
  const sentinel = useNextPageSentinel(feed);

  return (
    <Page>
      <PageHeader
        title={pub('title')}
        actions={
          <PublicationsToolbar
            state={toolbarState}
            onWindow={(window: WindowKey) => set({ window })}
            onSort={(sort: SortKey) => set({ sort })}
            onView={props.onViewChange ?? ((view: ViewKey) => set({ view }))}
            onTags={(next) => set({ tags: next.join(',') })}
            tagOptions={tagOptions.data ?? []}
            kindFilter={kindFilter}
            onKindFilter={(kind: KindFilterKey) => set({ kind })}
            recorte={recorte}
            onRecorte={(next: RecorteKey) => set({ recorte: next })}
          />
        }
      />
      <QueryStates
        query={feed as unknown as UseQueryResult<InfiniteData<Posts>, ApiFailureError>}
        isEmpty={(data) => data.pages.every((page) => page.rows.length === 0)}
        empty={<EmptyState title={pub('empty.title')} description={pub('empty.description')} />}
        loading={<PublicationsView view={toolbarState.view} entries={[]} loading />}
      >
        {(data) => (
          <>
            <PublicationsBody
              campaignId={props.campaignId}
              tenantId={props.tenantId}
              rows={data.pages.flatMap((page) => page.rows)}
              range={range}
              state={toolbarState}
              hrefFor={props.hrefFor}
              menu={menu}
            />
            {feed.hasNextPage && (
              <div ref={sentinel} className="flex justify-center py-4">
                <Button
                  variant="outline"
                  disabled={feed.isFetchingNextPage}
                  onClick={() => void feed.fetchNextPage()}
                >
                  {feed.isFetchingNextPage ? pub('loading.more') : pub('more')}
                </Button>
              </div>
            )}
          </>
        )}
      </QueryStates>
    </Page>
  );
}
